/**
 * Shared, read-only "customer file" body for a booking — the hydrated detail
 * shown on /sports/monitoring (calendar), inside the /sports/walk-in collect
 * modal, and anywhere else a booking needs a full money + customer breakdown.
 *
 * Display-only: any collect-payment UI stays in the consumer (e.g. the walk-in
 * modal renders <BookingDetailBody> then its own payment form below).
 */
import ProofPreviewButton from './ProofPreview';
import { fmt12 } from '../utils/sportsUtils';

const DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDateFull(ds) {
  if (!ds) return '';
  const d = new Date(ds + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return ds;
  return `${DOW_FULL[d.getDay()]}, ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
const num = (v) => parseFloat(v) || 0;

export function bookingBalance(b) {
  return b.balance_due != null
    ? Math.max(0, num(b.balance_due))
    : Math.max(0, num(b.amount) - num(b.amount_paid));
}
export function bookingIsPaid(b) {
  return b.payment_status === 'paid' || bookingBalance(b) <= 0.005;
}

function Row({ k, v, color }) {
  if (v === null || v === undefined || v === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid #191919' }}>
      <p style={{ fontSize: 12, color: '#555' }}>{k}</p>
      <p style={{ fontSize: 12.5, fontWeight: 700, color: color || '#ddd', textAlign: 'right' }}>{v}</p>
    </div>
  );
}

export default function BookingDetailBody({ booking: b, showProof = true }) {
  if (!b) return null;
  const balance = bookingBalance(b);
  const isPaid = bookingIsPaid(b);
  const courtTime = num(b.amount) - num(b.rental_total) + num(b.discount_amount);
  const history = (b.payment_history || []).slice().reverse();
  // No proof image? Explain WHY so staff know it's intentional, not missing.
  const paidViaPayMongo = String(b.payment_method).toLowerCase() === 'paymongo'
    || !!b.pay_session_id || num(b.online_charge) > 0
    || (b.payment_history || []).some(h => String(h.method).toLowerCase() === 'paymongo');

  return (
    <div>
      {/* Paid / balance banner */}
      <div style={{ borderRadius: 10, padding: '9px 14px', marginBottom: 12, background: isPaid ? '#00E67612' : '#E6394612', border: `1.5px solid ${isPaid ? '#00E67640' : '#E6394640'}` }}>
        <p style={{ fontSize: 13, fontWeight: 900, color: isPaid ? '#00E676' : '#E63946', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
          {isPaid ? 'PAID IN FULL' : `BALANCE DUE — ₱${balance.toLocaleString()}`}
        </p>
      </div>

      {/* Customer */}
      {(b.customer?.phone || b.customer?.email) && (
        <p style={{ fontSize: 12, color: '#737373', marginBottom: 8 }}>
          {b.customer?.phone || ''}{b.customer?.phone && b.customer?.email ? ' · ' : ''}{b.customer?.email || ''}
        </p>
      )}

      {/* Money + schedule breakdown */}
      <div style={{ marginBottom: 12 }}>
        <Row k="Court" v={`${b.court_name || ''}${b.sport ? ` (${b.sport})` : ''}`} />
        <Row k="Schedule" v={`${fmtDateFull(b.date)} · ${fmt12(b.start_time)} – ${fmt12(b.end_time)}`} />
        <Row k="Type" v={b.booking_type === 'competition' ? `Event${b.event_name ? ` — ${b.event_name}` : ''}` : b.booking_type === 'walk_in' || b.source === 'walk_in' ? 'Walk-in' : 'Online booking'} />
        <Row k="Status" v={(b.status || '').replace(/_/g, ' ')} />
        {(b.payment_method) && <Row k="Method" v={String(b.payment_method).toUpperCase()} />}
        <Row k="Court time" v={`₱${courtTime.toLocaleString()}`} />
        {num(b.rental_total) > 0 && <Row k="Equipment rental" v={`₱${num(b.rental_total).toLocaleString()}`} />}
        {num(b.discount_amount) > 0 && <Row k="Discount" v={`−₱${num(b.discount_amount).toLocaleString()}`} color="#00E676" />}
        {b.voucher_code && <Row k="Voucher" v={b.voucher_code} color="#00E676" />}
        <Row k="Total" v={`₱${num(b.amount).toLocaleString()}`} color="#fff" />
        <Row k="Paid so far" v={`₱${num(b.amount_paid).toLocaleString()}`} color="#00E676" />
        {!isPaid && <Row k="Balance" v={`₱${balance.toLocaleString()}`} color="#E63946" />}
        {b.ref_number && <Row k="Reference" v={b.ref_number} color="#E63946" />}
        {b.checked_in_at && <Row k="Checked in" v="🟢 Yes" color="#00E676" />}
        {b.notes && <Row k="Notes" v={b.notes} />}
      </div>

      {/* Payment history */}
      {history.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4 }}>Payment history</p>
          {history.map((h, i) => (
            <p key={h.id || i} style={{ fontSize: 11.5, color: '#737373', margin: '3px 0' }}>
              {num(h.amount) > 0 ? `₱${num(h.amount).toLocaleString()} · ${h.method}` : h.note}
              {h.tendered != null ? ` (received ₱${num(h.tendered).toLocaleString()}, change ₱${num(h.change).toLocaleString()})` : ''}
              {h.recorded_by ? ` — ${h.recorded_by}` : ''}{h.recorded_at ? ` · ${String(h.recorded_at).slice(0, 16).replace('T', ' ')}` : ''}
            </p>
          ))}
        </div>
      )}

      {/* GCash proof — only manual payments upload one. Explain the absence otherwise. */}
      {showProof && (b.proof_key ? (
        <div style={{ marginBottom: 4 }}>
          <ProofPreviewButton bookingId={b.id} label="View GCash Proof" />
        </div>
      ) : (
        <p style={{ fontSize: 11, marginBottom: 4, color: paidViaPayMongo ? '#00E676' : '#FFB300' }}>
          {paidViaPayMongo ? 'Paid via PayMongo — no proof needed' : 'No proof of payment submitted'}
        </p>
      ))}
    </div>
  );
}
